import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FiPlus, FiSearch, FiTrash2, FiUpload, FiX } from 'react-icons/fi';
import TelegramCaptionEditor from './TelegramCaptionEditor';
import TelegramMessagePreview from './TelegramMessagePreview';
import { singaporeNowDatetimeLocal, toSingaporeDatetimeLocalValue, formatSingaporeDateTimeDmyHm } from '../utils/singaporeTime';
import { getAutoBotMediaDisplayUrl } from '../utils/autoBotMediaUrl';

const API_BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
    'ngrok-skip-browser-warning': 'true',
});

const inputClass =
    'mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-bold py-2 px-3';
const selectClass =
    'mt-1 block w-full rounded-md border border-gray-300 bg-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-blue-700 font-semibold py-2 px-3';

const WEEKDAYS = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
];

const SCHEDULE_TYPES = [
    { value: 'every_n_minutes', label: 'Every N minutes' },
    { value: 'every_n_hours', label: 'Every N hours' },
    { value: 'every_n_days', label: 'Every N days' },
    { value: 'weekly_on_days', label: 'Weekly (days of week)' },
    { value: 'monthly_on_day', label: 'Monthly (day of Recurring Start Time)' },
];

function defaultSchedule() {
    return {
        scheduleType: 'every_n_days',
        interval: 1,
        daysOfWeek: [],
        recurringStartAt: singaporeNowDatetimeLocal(),
        recurringStopAt: '',
        openHour: '09:00',
        closeHour: '21:00',
    };
}

function scheduleFromTarget(target) {
    return {
        scheduleType: target?.scheduleType || 'every_n_days',
        interval: target?.interval || 1,
        daysOfWeek: Array.isArray(target?.daysOfWeek) ? target.daysOfWeek : [],
        recurringStartAt: target?.recurringStartAt || singaporeNowDatetimeLocal(),
        recurringStopAt: target?.recurringStopAt || '',
        openHour: target?.openHour || '09:00',
        closeHour: target?.closeHour || '21:00',
    };
}

function schedulesEqual(a, b) {
    const daysA = [...(a.daysOfWeek || [])].sort().join(',');
    const daysB = [...(b.daysOfWeek || [])].sort().join(',');
    return (
        a.scheduleType === b.scheduleType
        && Number(a.interval) === Number(b.interval)
        && String(a.recurringStartAt || '') === String(b.recurringStartAt || '')
        && String(a.recurringStopAt || '') === String(b.recurringStopAt || '')
        && String(a.openHour || '') === String(b.openHour || '')
        && String(a.closeHour || '') === String(b.closeHour || '')
        && daysA === daysB
    );
}

function allTargetsSameSchedule(targets = []) {
    if (targets.length <= 1) return true;
    const first = scheduleFromTarget(targets[0]);
    return targets.every((t) => schedulesEqual(scheduleFromTarget(t), first));
}

function applyScheduleToTargets(targets, schedule) {
    return (targets || []).map((t) => ({ ...t, ...schedule }));
}

function emptyTarget(schedule = defaultSchedule()) {
    return {
        chat: '',
        chatTitle: '',
        active: true,
        ...schedule,
        recurringStartAtIso: null,
        nextRunAt: null,
        lastRunAt: null,
        lastSentAt: null,
        lastRunStatus: '',
        lastError: '',
    };
}

function formatHourAmPm(value) {
    const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
    if (!match) return '';
    let hour = Number(match[1]);
    const minute = match[2];
    const period = hour >= 12 ? 'pm' : 'am';
    hour = hour % 12;
    if (hour === 0) hour = 12;
    return `${hour}:${minute}${period}`;
}

function sendWindowLabel(target) {
    const open = formatHourAmPm(target.openHour);
    const close = formatHourAmPm(target.closeHour);
    if (!open && !close) return 'All day';
    if (!open || !close) return `${open || '—'} - ${close || '—'}`;
    return `${open} - ${close}`;
}

function scheduleLabel(target) {
    const type = target.scheduleType || '—';
    if (['every_n_minutes', 'every_n_hours', 'every_n_days'].includes(type)) {
        return `${type} - ${target.interval || 1}`;
    }
    if (type === 'weekly_on_days') {
        const days = (target.daysOfWeek || [])
            .map((d) => WEEKDAYS.find((w) => w.value === d)?.label || d)
            .join(', ');
        return `${type} - ${days || '—'}`;
    }
    return type;
}

function emptyForm() {
    return {
        autobotConfigName: '',
        active: false,
        remarks: '',
        godMember: null,
        godMemberName: '',
        contentMode: 'text_only',
        chatText: '',
        caption: '',
        media: [],
        buttons: [],
        parseMode: 'HTML',
        deleteLastMessage: false,
        targets: [],
    };
}

function buttonsFromApi(rows = []) {
    const out = [];
    rows.forEach((row, rowIdx) => {
        (row.buttons || []).forEach((b) => {
            out.push({ text: b.text || '', url: b.url || '', row: rowIdx });
        });
    });
    return out;
}

function memberLabel(m) {
    if (!m) return '';
    return m.fullName || [m.godID, m.godName, m.webID].filter(Boolean).join(' - ');
}

function AutoBotMediaThumb({ item, onRemove, onPreview, readOnly }) {
    const isVideo = item?.mediaType === 'video';
    const src = getAutoBotMediaDisplayUrl(item);
    return (
        <div className="relative group">
            {isVideo ? (
                <video
                    src={src}
                    className="w-24 h-24 object-cover rounded border border-gray-200 bg-black cursor-pointer hover:ring-2 hover:ring-blue-400"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPreview && onPreview(item);
                    }}
                    title="Click to preview"
                />
            ) : (
                <img
                    src={src}
                    alt={item.filename || ''}
                    className="w-24 h-24 object-cover rounded border border-gray-200 cursor-pointer hover:ring-2 hover:ring-blue-400"
                    onClick={(e) => {
                        e.stopPropagation();
                        onPreview && onPreview(item);
                    }}
                    title="Click to preview"
                />
            )}
            {!readOnly ? (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove();
                    }}
                    className="absolute -top-1 -right-1 p-0.5 bg-red-500 text-white rounded-full opacity-90 group-hover:opacity-100"
                    aria-label="Remove media"
                >
                    <FiX className="w-4 h-4" />
                </button>
            ) : null}
        </div>
    );
}

const GROUP_ACTIVE_NOTE = 'You can turn off this to stop the schedule for this group temporary';
const INTERVAL_SCHEDULE_TYPES = ['every_n_minutes', 'every_n_hours', 'every_n_days'];

function ScheduleFields({ schedule, disabled, onChange, chatSlot = null }) {
    const showInterval = INTERVAL_SCHEDULE_TYPES.includes(schedule.scheduleType);
    return (
        <div className="space-y-3">
            <div className={`grid grid-cols-1 gap-3 ${chatSlot ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}>
                {chatSlot ? <div className="relative min-w-0">{chatSlot}</div> : null}
                <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-600">Schedule</label>
                    <select
                        disabled={disabled}
                        value={schedule.scheduleType}
                        onChange={(e) => onChange({ scheduleType: e.target.value })}
                        className={selectClass}
                    >
                        {SCHEDULE_TYPES.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-600">Every N</label>
                    <input
                        type="number"
                        min={1}
                        disabled={disabled || !showInterval}
                        value={showInterval ? schedule.interval : ''}
                        onChange={(e) => onChange({ interval: e.target.value })}
                        className={inputClass}
                        placeholder={showInterval ? '' : '—'}
                    />
                </div>
                <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-600">Recurring Start Time (SG)</label>
                    <input
                        type="datetime-local"
                        disabled={disabled}
                        value={schedule.recurringStartAt}
                        onChange={(e) => onChange({ recurringStartAt: e.target.value })}
                        className={inputClass}
                    />
                </div>
            </div>
            <div className="grid grid-cols-1 gap-3 xl:grid-cols-3">
                <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-600">Recurring Stop Time (SG)</label>
                    <input
                        type="datetime-local"
                        disabled={disabled}
                        value={schedule.recurringStopAt || ''}
                        onChange={(e) => onChange({ recurringStopAt: e.target.value })}
                        className={inputClass}
                    />
                </div>
                <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-600">Open hour (SG)</label>
                    <input
                        type="time"
                        disabled={disabled}
                        value={schedule.openHour || ''}
                        onChange={(e) => onChange({ openHour: e.target.value })}
                        className={inputClass}
                    />
                </div>
                <div className="min-w-0">
                    <label className="block text-xs font-medium text-gray-600">Close hour (SG)</label>
                    <input
                        type="time"
                        disabled={disabled}
                        value={schedule.closeHour || ''}
                        onChange={(e) => onChange({ closeHour: e.target.value })}
                        className={inputClass}
                    />
                    <p className="mt-1 text-[11px] text-gray-500">Leave both empty for all day. Close is exclusive (9:00pm means last send before 9:00pm).</p>
                </div>
            </div>
            {schedule.scheduleType === 'weekly_on_days' ? (
                <div className="flex flex-wrap gap-2">
                    {WEEKDAYS.map((d) => (
                        <label key={d.value} className="inline-flex items-center gap-1 text-sm">
                            <input
                                type="checkbox"
                                disabled={disabled}
                                checked={(schedule.daysOfWeek || []).includes(d.value)}
                                onChange={(e) => {
                                    const next = new Set(schedule.daysOfWeek || []);
                                    if (e.target.checked) next.add(d.value);
                                    else next.delete(d.value);
                                    onChange({ daysOfWeek: [...next].sort() });
                                }}
                            />
                            {d.label}
                        </label>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function buttonsToApi(buttons = []) {
    const map = new Map();
    buttons.forEach((b) => {
        if (!String(b.text || '').trim() || !String(b.url || '').trim()) return;
        const row = Number(b.row) || 0;
        if (!map.has(row)) map.set(row, []);
        map.get(row).push({ text: String(b.text).trim(), url: String(b.url).trim() });
    });
    return [...map.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([, rowButtons]) => ({ buttons: rowButtons }));
}

function formFromDoc(doc) {
    return {
        autobotConfigName: doc.autobotConfigName || '',
        active: Boolean(doc.active),
        remarks: doc.remarks || '',
        godMember: doc.godMember?._id || doc.godMember || null,
        godMemberName: doc.godMember?.fullName || '',
        contentMode: doc.contentMode || 'text_only',
        chatText: doc.chatText || '',
        caption: doc.caption || '',
        media: Array.isArray(doc.media) ? doc.media : [],
        buttons: buttonsFromApi(doc.buttons),
        parseMode: doc.parseMode || 'HTML',
        deleteLastMessage: Boolean(doc.deleteLastMessage),
        targets: (doc.targets || []).map((t) => ({
            _id: t._id,
            chat: t.chat?._id || t.chat || '',
            chatTitle: t.chat?.chatTitle || t.chat?.chatUsername || '',
            active: t.active !== false,
            scheduleType: t.schedule?.type || 'every_n_days',
            interval: t.schedule?.interval || 1,
            daysOfWeek: t.schedule?.daysOfWeek || [],
            recurringStartAt: toSingaporeDatetimeLocalValue(t.recurringStartAt) || singaporeNowDatetimeLocal(),
            recurringStartAtIso: t.recurringStartAt || null,
            recurringStopAt: toSingaporeDatetimeLocalValue(t.recurringStopAt) || '',
            recurringStopAtIso: t.recurringStopAt || null,
            openHour: t.openHour || '',
            closeHour: t.closeHour || '',
            nextRunAt: t.nextRunAt || null,
            lastRunAt: t.lastRunAt || null,
            lastSentAt: t.lastSentAt || null,
            lastRunStatus: t.lastRunStatus || '',
            lastError: t.lastError || '',
        })),
    };
}

const GodAutoBotModal = ({ mode, initialDoc, onClose, onSaved }) => {
    const readOnly = mode === 'view';
    const [form, setForm] = useState(() => (initialDoc ? formFromDoc(initialDoc) : emptyForm()));
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [memberSearch, setMemberSearch] = useState('');
    const [memberResults, setMemberResults] = useState([]);
    const [showMemberDropdown, setShowMemberDropdown] = useState(false);
    const [memberSearching, setMemberSearching] = useState(false);
    const [chats, setChats] = useState([]);
    const [chatSearchByIndex, setChatSearchByIndex] = useState({});
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);
    const mediaInputRef = useRef(null);
    const [sameSchedule, setSameSchedule] = useState(() => (
        initialDoc ? allTargetsSameSchedule(formFromDoc(initialDoc).targets) : true
    ));
    const [sharedSchedule, setSharedSchedule] = useState(() => {
        const targets = initialDoc ? formFromDoc(initialDoc).targets : [];
        return targets[0] ? scheduleFromTarget(targets[0]) : defaultSchedule();
    });
    const [activeHintIndex, setActiveHintIndex] = useState(null);
    const activeHintTimer = useRef(null);
    const [lightboxItem, setLightboxItem] = useState(null);

    useEffect(() => () => {
        if (activeHintTimer.current) clearTimeout(activeHintTimer.current);
    }, []);

    useEffect(() => {
        const loadChats = async () => {
            try {
                const res = await axios.get(`${API_BASE}/api/god-autobots/chats?limit=300`, { headers: authHeaders() });
                if (res.data.success) setChats(res.data.data || []);
            } catch {
                // picker still usable if empty
            }
        };
        loadChats();
    }, []);

    const searchMembers = async () => {
        const term = memberSearch.trim();
        if (!term || readOnly) return;
        setMemberSearching(true);
        setShowMemberDropdown(true);
        try {
            const res = await axios.get(
                `${API_BASE}/api/godmembers?search=${encodeURIComponent(term)}&searchType=name&limit=20`,
                { headers: authHeaders() }
            );
            setMemberResults(res.data.success ? (res.data.data || []) : []);
        } catch {
            setMemberResults([]);
        } finally {
            setMemberSearching(false);
        }
    };

    const hideButtons = form.contentMode === 'multimedia';
    const bodyText = form.contentMode === 'text_only' ? form.chatText : form.caption;
    const textLimit = form.contentMode === 'text_only' ? 4096 : 1024;
    const textOverLimit = String(bodyText || '').length > textLimit;
    const previewChannel = form.targets.find((t) => t.chatTitle)?.chatTitle || 'Preview';

    const title = useMemo(() => {
        if (mode === 'create') return 'Create AutoBot Config';
        if (mode === 'edit') return 'Edit AutoBot Config';
        return 'View AutoBot Config';
    }, [mode]);

    const setField = (name, value) => setForm((prev) => ({ ...prev, [name]: value }));

    const handleContentMode = (nextMode) => {
        setForm((prev) => {
            let media = prev.media;
            if (nextMode === 'text_only') media = [];
            else if (nextMode === 'one_media') media = media.slice(0, 1);
            return {
                ...prev,
                contentMode: nextMode,
                media,
                buttons: nextMode === 'multimedia' ? [] : prev.buttons,
            };
        });
    };

    const handleAddButton = () => {
        const nextRow = form.buttons.length
            ? Math.max(...form.buttons.map((b) => Number(b.row) || 0)) + 1
            : 0;
        setField('buttons', [...form.buttons, { text: '', url: '', row: nextRow }]);
    };

    const handleButtonChange = (index, field, value) => {
        setField(
            'buttons',
            form.buttons.map((btn, i) => (i === index ? { ...btn, [field]: value } : btn))
        );
    };

    const handleUploadMedia = async (fileList) => {
        const files = Array.from(fileList || []).filter((f) => f && (f.type.startsWith('image/') || f.type.startsWith('video/')));
        if (!files.length || readOnly) return;
        setError('');
        setUploading(true);
        try {
            const uploaded = [];
            const toUpload = form.contentMode === 'one_media' ? files.slice(0, 1) : files;
            for (const file of toUpload) {
                if (form.contentMode === 'multimedia' && form.media.length + uploaded.length >= 10) break;
                const body = new FormData();
                body.append('file', file);
                const res = await axios.post(`${API_BASE}/api/god-autobots/media`, body, {
                    headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
                });
                if (!res.data.success) throw new Error(res.data.error || 'Upload failed');
                uploaded.push(res.data.data);
            }
            setForm((prev) => {
                if (prev.contentMode === 'one_media') return { ...prev, media: uploaded.slice(0, 1) };
                const next = [...prev.media, ...uploaded].slice(0, 10);
                return { ...prev, media: next };
            });
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setUploading(false);
        }
    };

    const updateTarget = (index, patch) => {
        setForm((prev) => ({
            ...prev,
            targets: prev.targets.map((t, i) => (i === index ? { ...t, ...patch } : t)),
        }));
    };

    const updateSharedSchedule = (patch) => {
        const next = { ...sharedSchedule, ...patch };
        setSharedSchedule(next);
        setForm((prev) => ({
            ...prev,
            targets: applyScheduleToTargets(prev.targets, next),
        }));
    };

    const handleScheduleMode = (nextSame) => {
        if (nextSame) {
            const fromFirst = form.targets[0] ? scheduleFromTarget(form.targets[0]) : sharedSchedule;
            setSharedSchedule(fromFirst);
            setForm((prev) => ({
                ...prev,
                targets: applyScheduleToTargets(prev.targets, fromFirst),
            }));
        }
        setSameSchedule(nextSame);
    };

    const handleAddGroup = () => {
        const schedule = sameSchedule ? sharedSchedule : defaultSchedule();
        setField('targets', [...form.targets, emptyTarget(schedule)]);
    };

    const showGroupActiveHint = (index) => {
        setActiveHintIndex(index);
        if (activeHintTimer.current) clearTimeout(activeHintTimer.current);
        activeHintTimer.current = setTimeout(() => setActiveHintIndex(null), 4000);
    };

    const filteredChats = (q) => {
        const term = String(q || '').trim().toLowerCase();
        if (!term) return chats.slice(0, 30);
        return chats
            .filter((c) =>
                [c.chatTitle, c.chatUsername, c.chatID]
                    .filter(Boolean)
                    .some((v) => String(v).toLowerCase().includes(term))
            )
            .slice(0, 30);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (readOnly) return;
        setError('');
        if (!form.autobotConfigName.trim()) {
            setError('Name is required');
            return;
        }
        if (form.contentMode === 'one_media' && form.media.length !== 1) {
            setError('One media mode needs exactly one photo or video');
            return;
        }
        if (form.contentMode === 'multimedia' && (form.media.length < 2 || form.media.length > 10)) {
            setError('Multimedia mode needs 2–10 photos/videos');
            return;
        }
        const limit = form.contentMode === 'text_only' ? 4096 : 1024;
        const text = form.contentMode === 'text_only' ? form.chatText : form.caption;
        if (String(text || '').length > limit) {
            setError(
                form.contentMode === 'text_only'
                    ? `Chat text is over ${limit} characters`
                    : `Caption is over ${limit} characters`
            );
            return;
        }
        for (const t of form.targets) {
            if (!t.chat) {
                setError('Each group needs a chat selected');
                return;
            }
        }
        const targetsForSave = sameSchedule
            ? applyScheduleToTargets(form.targets, sharedSchedule)
            : form.targets;
        for (const t of targetsForSave) {
            if (t.scheduleType === 'weekly_on_days' && !(t.daysOfWeek || []).length) {
                setError('Weekly schedule needs at least one weekday');
                return;
            }
            const hasOpen = Boolean(String(t.openHour || '').trim());
            const hasClose = Boolean(String(t.closeHour || '').trim());
            if (hasOpen !== hasClose) {
                setError('Send window needs both Open hour and Close hour, or leave both empty for all day');
                return;
            }
        }

        const payload = {
            autobotConfigName: form.autobotConfigName.trim(),
            active: form.active,
            remarks: form.remarks,
            godMember: form.godMember || null,
            contentMode: form.contentMode,
            chatText: form.contentMode === 'text_only' ? form.chatText : '',
            caption: form.contentMode === 'text_only' ? '' : form.caption,
            media: form.contentMode === 'text_only' ? [] : form.media,
            buttons: hideButtons ? [] : buttonsToApi(form.buttons),
            parseMode: form.parseMode,
            deleteLastMessage: form.deleteLastMessage,
            targets: targetsForSave.map((t) => ({
                ...(mode === 'create' ? {} : { _id: t._id }),
                chat: t.chat,
                active: t.active,
                schedule: {
                    type: t.scheduleType,
                    interval: Number(t.interval) || 1,
                    daysOfWeek: t.scheduleType === 'weekly_on_days' ? t.daysOfWeek : [],
                },
                recurringStartAt: t.recurringStartAt,
                recurringStopAt: t.recurringStopAt || null,
                openHour: t.openHour || '',
                closeHour: t.closeHour || '',
            })),
        };

        setSaving(true);
        try {
            if (mode === 'create') {
                await axios.post(`${API_BASE}/api/god-autobots`, payload, { headers: authHeaders() });
            } else {
                await axios.put(`${API_BASE}/api/god-autobots/${initialDoc._id}`, payload, { headers: authHeaders() });
            }
            onSaved();
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    if (readOnly) {
        return (
            <div className="fixed inset-0 z-[200] flex items-start justify-center overflow-y-auto bg-black/50 p-4 pt-10">
                <div className="mb-10 w-full max-w-6xl rounded-lg border border-gray-200 bg-white p-6 shadow-xl">
                    <div className="mb-4 flex items-start justify-between gap-4">
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                            <p className="text-sm text-gray-600">{form.autobotConfigName || '—'}</p>
                        </div>
                        <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
                            <FiX />
                        </button>
                    </div>
                    <div className="mx-auto mb-6 max-w-sm">
                        <TelegramMessagePreview
                            contentMode={form.contentMode}
                            text={bodyText}
                            media={form.media}
                            buttons={form.buttons}
                            channelName={previewChannel}
                            hideButtons={hideButtons}
                        />
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Group</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Schedule</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">StartDate</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">StopDate</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Send window</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">NextRunAt</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">lastRunAt</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">lastSentAt</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">lastRunStatus</th>
                                    <th className="px-3 py-2 text-left font-semibold text-gray-700">lastError</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 bg-white">
                                {form.targets.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} className="px-3 py-4 text-gray-500">No groups.</td>
                                    </tr>
                                ) : (
                                    form.targets.map((target, index) => (
                                        <tr key={target._id || index}>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{target.chatTitle || '—'}</td>
                                            <td className="px-3 py-2 text-gray-800">{scheduleLabel(target)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{formatSingaporeDateTimeDmyHm(target.recurringStartAtIso || target.recurringStartAt)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{formatSingaporeDateTimeDmyHm(target.recurringStopAtIso || target.recurringStopAt)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{sendWindowLabel(target)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{formatSingaporeDateTimeDmyHm(target.nextRunAt)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{formatSingaporeDateTimeDmyHm(target.lastRunAt)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{formatSingaporeDateTimeDmyHm(target.lastSentAt)}</td>
                                            <td className="whitespace-nowrap px-3 py-2 text-gray-800">{target.lastRunStatus || '—'}</td>
                                            <td className="max-w-xs break-words px-3 py-2 text-gray-800">{target.lastError || '—'}</td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="mt-6 flex justify-end">
                        <button type="button" onClick={onClose} className="rounded-md bg-gray-100 px-4 py-2 text-gray-700 hover:bg-gray-200">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[200] bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full">
            <div className="relative top-6 mx-auto p-6 border w-full max-w-6xl shadow-lg rounded-md bg-white mb-10">
                <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2">
                    <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                            type="checkbox"
                            checked={form.active}
                            disabled={readOnly}
                            onChange={(e) => setField('active', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600"
                        />
                        Active
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                        <input
                            type="checkbox"
                            checked={form.deleteLastMessage}
                            disabled={readOnly}
                            onChange={(e) => setField('deleteLastMessage', e.target.checked)}
                            className="rounded border-gray-300 text-blue-600"
                        />
                        Delete last message
                    </label>
                    <button type="button" onClick={onClose} className="ml-auto text-gray-500 hover:text-gray-700">
                        <FiX />
                    </button>
                </div>

                {error ? <div className="mb-4 rounded-md bg-red-100 p-3 text-sm text-red-700">{error}</div> : null}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input
                                type="text"
                                value={form.autobotConfigName}
                                disabled={readOnly}
                                onChange={(e) => setField('autobotConfigName', e.target.value)}
                                className={inputClass}
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">God Member (optional)</label>
                            {form.godMemberName ? (
                                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2 min-h-[42px]">
                                    <span className="text-sm text-gray-800 truncate">{form.godMemberName}</span>
                                    {!readOnly ? (
                                        <button
                                            type="button"
                                            className="ml-2 shrink-0 text-sm text-blue-600"
                                            onClick={() => {
                                                setField('godMember', null);
                                                setField('godMemberName', '');
                                                setMemberSearch('');
                                                setMemberResults([]);
                                                setShowMemberDropdown(false);
                                            }}
                                        >
                                            Change
                                        </button>
                                    ) : null}
                                </div>
                            ) : (
                                <div className="relative mt-1 flex gap-1">
                                    <input
                                        type="text"
                                        disabled={readOnly}
                                        value={memberSearch}
                                        onChange={(e) => {
                                            setMemberSearch(e.target.value);
                                            setShowMemberDropdown(false);
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                searchMembers();
                                            }
                                        }}
                                        placeholder="GodID, name, old name or webID"
                                        className={`${inputClass} mt-0 min-w-0`}
                                    />
                                    {!readOnly ? (
                                        <button
                                            type="button"
                                            onClick={searchMembers}
                                            className="mt-0 p-2 rounded-md border border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-600 shrink-0"
                                            aria-label="Search member"
                                        >
                                            <FiSearch className="w-4 h-4" />
                                        </button>
                                    ) : null}
                                    {showMemberDropdown ? (
                                        <ul className="absolute z-20 left-0 right-0 top-full mt-1 max-h-48 overflow-auto rounded-md bg-white shadow ring-1 ring-black/5">
                                            {memberSearching ? (
                                                <li className="px-3 py-2 text-sm text-gray-500">Loading...</li>
                                            ) : memberResults.length > 0 ? (
                                                memberResults.map((m) => (
                                                    <li
                                                        key={m._id}
                                                        className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-50"
                                                        onClick={() => {
                                                            setField('godMember', m._id);
                                                            setField('godMemberName', memberLabel(m));
                                                            setMemberSearch('');
                                                            setMemberResults([]);
                                                            setShowMemberDropdown(false);
                                                        }}
                                                    >
                                                        {memberLabel(m)}
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="px-3 py-2 text-sm text-gray-500">No matches found</li>
                                            )}
                                        </ul>
                                    ) : null}
                                </div>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Content type</label>
                            <select
                                value={form.contentMode}
                                disabled={readOnly}
                                onChange={(e) => handleContentMode(e.target.value)}
                                className={selectClass}
                            >
                                <option value="text_only">Text only</option>
                                <option value="one_media">One media</option>
                                <option value="multimedia">Multimedia (album, no buttons)</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-2">
                        <div className="space-y-4">
                            {form.contentMode !== 'text_only' ? (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Media</label>
                                    {!readOnly ? (
                                        <div
                                            onDrop={(e) => {
                                                e.preventDefault();
                                                setDragOver(false);
                                                handleUploadMedia(e.dataTransfer.files);
                                            }}
                                            onDragOver={(e) => {
                                                e.preventDefault();
                                                setDragOver(true);
                                            }}
                                            onDragLeave={() => setDragOver(false)}
                                            onClick={() => mediaInputRef.current && mediaInputRef.current.click()}
                                            className={`mt-1 border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                                                dragOver ? 'border-blue-500 bg-blue-50' : 'border-gray-300 bg-gray-50'
                                            } ${uploading || (form.contentMode === 'multimedia' && form.media.length >= 10) ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}
                                        >
                                            <input
                                                ref={mediaInputRef}
                                                type="file"
                                                accept="image/*,video/*"
                                                multiple={form.contentMode === 'multimedia'}
                                                disabled={uploading || (form.contentMode === 'multimedia' && form.media.length >= 10)}
                                                className="hidden"
                                                onChange={(e) => {
                                                    handleUploadMedia(e.target.files);
                                                    e.target.value = '';
                                                }}
                                            />
                                            <FiUpload className="mx-auto h-8 w-8 text-gray-400" />
                                            <p className="mt-1 text-sm text-gray-600">
                                                Drag & drop or click to upload {form.contentMode === 'multimedia' ? 'photos/videos' : 'a photo or video'}
                                            </p>
                                            <p className="mt-0.5 text-xs text-gray-500">
                                                {form.contentMode === 'one_media'
                                                    ? 'Exactly 1 file. New upload replaces the current one.'
                                                    : '2–10 files. Videos preview with autoplay and loop.'}
                                            </p>
                                        </div>
                                    ) : null}
                                    {uploading ? <p className="mt-1 text-sm text-blue-600">Uploading…</p> : null}
                                    {form.media.length > 0 ? (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {form.media.map((item, idx) => (
                                                <AutoBotMediaThumb
                                                    key={`${item.url}-${idx}`}
                                                    item={item}
                                                    readOnly={readOnly}
                                                    onPreview={setLightboxItem}
                                                    onRemove={() => setField('media', form.media.filter((_, i) => i !== idx))}
                                                />
                                            ))}
                                        </div>
                                    ) : null}
                                </div>
                            ) : null}

                            <TelegramCaptionEditor
                                label={form.contentMode === 'text_only' ? 'Chat text' : 'Caption'}
                                value={bodyText}
                                disabled={readOnly}
                                maxLength={textLimit}
                                onChange={(next) =>
                                    setField(form.contentMode === 'text_only' ? 'chatText' : 'caption', next)
                                }
                                placeholder={form.contentMode === 'text_only' ? 'Telegram message…' : 'Telegram caption…'}
                            />

                            {hideButtons ? (
                                <p className="rounded-md border border-dashed border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900">
                                    No buttons for multimedia albums.
                                </p>
                            ) : (
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-semibold text-gray-800">Buttons</h3>
                                        {!readOnly ? (
                                            <button
                                                type="button"
                                                onClick={handleAddButton}
                                                className="inline-flex items-center gap-1.5 rounded-md bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-700"
                                            >
                                                <FiPlus className="h-4 w-4" />
                                                Add Button
                                            </button>
                                        ) : null}
                                    </div>
                                    {form.buttons.length === 0 ? (
                                        <p className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-3 py-4 text-sm text-gray-500">
                                            No buttons yet. Click Add Button to create an inline keyboard row.
                                        </p>
                                    ) : (
                                        form.buttons.map((btn, index) => (
                                            <div key={`btn-${index}`} className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                                                <div className="flex items-start justify-between">
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                                        Button {index + 1}
                                                    </p>
                                                    {!readOnly ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setField('buttons', form.buttons.filter((_, i) => i !== index))}
                                                            className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                                                        >
                                                            <FiTrash2 className="h-3.5 w-3.5" />
                                                            Remove
                                                        </button>
                                                    ) : null}
                                                </div>
                                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600">Button text</label>
                                                        <input type="text" disabled={readOnly} value={btn.text} onChange={(e) => handleButtonChange(index, 'text', e.target.value)} className={inputClass} />
                                                    </div>
                                                    <div>
                                                        <label className="block text-xs font-medium text-gray-600">Row (0 = first row)</label>
                                                        <input type="number" min={0} disabled={readOnly} value={btn.row} onChange={(e) => handleButtonChange(index, 'row', e.target.value)} className={inputClass} />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600">URL</label>
                                                    <input type="url" disabled={readOnly} value={btn.url} onChange={(e) => handleButtonChange(index, 'url', e.target.value)} className={inputClass} placeholder="https://…" />
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}
                        </div>

                        <TelegramMessagePreview
                            contentMode={form.contentMode}
                            text={bodyText}
                            media={form.media}
                            buttons={form.buttons}
                            channelName={previewChannel}
                            hideButtons={hideButtons}
                        />
                    </div>

                    <div>
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                            <h3 className="text-sm font-semibold text-gray-800">Groups</h3>
                            {!readOnly ? (
                                <button
                                    type="button"
                                    onClick={handleAddGroup}
                                    className="inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
                                >
                                    <FiPlus className="h-4 w-4" />
                                    Add Group
                                </button>
                            ) : null}
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700">Schedule for groups</label>
                            <select
                                value={sameSchedule ? 'same' : 'different'}
                                disabled={readOnly}
                                onChange={(e) => handleScheduleMode(e.target.value === 'same')}
                                className={selectClass}
                            >
                                <option value="same">All Groups Same Schedule</option>
                                <option value="different">All Groups Different Schedule</option>
                            </select>
                        </div>
                        {form.targets.length === 0 ? (
                            <p className="text-sm text-gray-500">No groups yet.</p>
                        ) : (
                            <div className="space-y-4">
                                {form.targets.map((target, index) => {
                                    const chatField = (
                                        <>
                                            <label className="block text-xs font-medium text-gray-600">Chat</label>
                                            <input
                                                type="text"
                                                disabled={readOnly}
                                                value={chatSearchByIndex[index] ?? target.chatTitle}
                                                onFocus={() => {
                                                    if (!readOnly) {
                                                        setChatSearchByIndex((prev) => ({
                                                            ...prev,
                                                            [index]: prev[index] != null ? prev[index] : (target.chatTitle || ''),
                                                        }));
                                                    }
                                                }}
                                                onChange={(e) => {
                                                    setChatSearchByIndex((prev) => ({ ...prev, [index]: e.target.value }));
                                                    updateTarget(index, { chat: '', chatTitle: e.target.value });
                                                }}
                                                placeholder="Search chat title…"
                                                className={inputClass}
                                            />
                                            {!readOnly && chatSearchByIndex[index] != null ? (
                                                <ul className="absolute z-10 mt-1 max-h-40 w-full overflow-auto rounded-md bg-white shadow ring-1 ring-black/5">
                                                    {filteredChats(chatSearchByIndex[index]).map((c) => (
                                                        <li
                                                            key={c._id}
                                                            className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-50"
                                                            onClick={() => {
                                                                updateTarget(index, { chat: c._id, chatTitle: c.chatTitle || c.chatUsername || c.chatID });
                                                                setChatSearchByIndex((prev) => {
                                                                    const next = { ...prev };
                                                                    delete next[index];
                                                                    return next;
                                                                });
                                                            }}
                                                        >
                                                            {c.chatTitle || c.chatUsername || c.chatID}
                                                        </li>
                                                    ))}
                                                </ul>
                                            ) : null}
                                        </>
                                    );
                                    return (
                                    <div key={target._id || index} className="rounded-lg border border-gray-200 p-4 space-y-3">
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                            <p className="text-xs font-semibold uppercase text-gray-500">Group {index + 1}</p>
                                            <label
                                                className="group/active relative inline-flex items-center gap-2 text-sm text-gray-700"
                                                title={GROUP_ACTIVE_NOTE}
                                            >
                                                <input
                                                    type="checkbox"
                                                    disabled={readOnly}
                                                    checked={target.active}
                                                    onChange={(e) => {
                                                        updateTarget(index, { active: e.target.checked });
                                                        showGroupActiveHint(index);
                                                    }}
                                                    className="rounded border-gray-300 text-blue-600"
                                                />
                                                Active
                                                <span
                                                    className={`max-w-xs text-[11px] leading-snug text-gray-500 ${
                                                        activeHintIndex === index
                                                            ? 'opacity-100'
                                                            : 'opacity-0 pointer-events-none group-hover/active:opacity-100 group-hover/active:pointer-events-auto'
                                                    }`}
                                                >
                                                    {GROUP_ACTIVE_NOTE}
                                                </span>
                                            </label>
                                            {!readOnly ? (
                                                <button type="button" className="ml-auto text-red-600 text-sm" onClick={() => setField('targets', form.targets.filter((_, i) => i !== index))}>
                                                    Remove
                                                </button>
                                            ) : null}
                                        </div>
                                        <ScheduleFields
                                            chatSlot={chatField}
                                            schedule={sameSchedule ? sharedSchedule : scheduleFromTarget(target)}
                                            disabled={readOnly || (sameSchedule && index > 0)}
                                            onChange={sameSchedule ? updateSharedSchedule : (patch) => updateTarget(index, patch)}
                                        />
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700">Remarks</label>
                        <textarea disabled={readOnly} value={form.remarks} onChange={(e) => setField('remarks', e.target.value)} rows={2} className={inputClass} />
                    </div>

                    <div className="flex justify-end gap-3 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200">
                            {readOnly ? 'Close' : 'Cancel'}
                        </button>
                        {!readOnly ? (
                            <button type="submit" disabled={saving || textOverLimit} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300">
                                {saving ? 'Saving…' : mode === 'create' ? 'Create' : 'Save'}
                            </button>
                        ) : null}
                    </div>
                </form>
            </div>
            {lightboxItem ? (
                <div
                    className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setLightboxItem(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Media preview"
                >
                    {lightboxItem.mediaType === 'video' ? (
                        <video
                            src={getAutoBotMediaDisplayUrl(lightboxItem)}
                            className="max-w-full max-h-[90vh] rounded shadow-lg bg-black"
                            autoPlay
                            loop
                            muted
                            playsInline
                            controls
                            onClick={(e) => e.stopPropagation()}
                        />
                    ) : (
                        <img
                            src={getAutoBotMediaDisplayUrl(lightboxItem)}
                            alt={lightboxItem.filename || ''}
                            className="max-w-full max-h-[90vh] w-auto h-auto object-contain rounded shadow-lg"
                            onClick={(e) => e.stopPropagation()}
                        />
                    )}
                    <button
                        type="button"
                        onClick={() => setLightboxItem(null)}
                        className="absolute top-4 right-4 p-2 rounded-full bg-white/90 text-gray-800 hover:bg-white"
                        aria-label="Close"
                    >
                        <FiX className="w-6 h-6" />
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default GodAutoBotModal;
